<template>
  <div class="upload-container container">
    <!-- Overlay de carga -->
    <div v-if="uploading" class="upload-overlay">
      <div class="upload-overlay-content">
        <i class="pi pi-spin pi-spinner upload-spinner"></i>
        <h3>Subiendo archivos</h3>
        <p v-if="totalFiles > 0">Progreso: {{ uploadedFilesCount }}/{{ totalFiles }}</p>
        <ProgressBar :value="uploadProgress" :showValue="true" style="height: 10px; margin-top: 10px;" />
      </div>
    </div>
    
    <!-- Overlay de éxito -->
    <div v-if="showSuccessMessage" class="success-overlay" @click="hideSuccessMessage">
      <div class="success-overlay-content">
        <i class="pi pi-check-circle success-icon"></i>
        <h3>¡Completado!</h3>
        <p>{{ uploadedFilesCount }} archivos han sido subidos correctamente.</p>
        <Button label="Aceptar" @click="hideSuccessMessage" class="p-button-success" />
      </div>
    </div>
    
    <div class="page-header">
      <h1><i class="pi pi-cloud-upload"></i> Subir Archivos</h1>
      <p>Carga y gestiona archivos biológicos para el análisis de datos NGS</p>
      <div class="bucket-info">
        <span class="country-badge"><i class="pi pi-map-marker"></i> País: <strong>{{ getCountryFromBucket(bucketName) }}</strong></span>
        <span class="bucket-badge"><i class="pi pi-database"></i> Bucket: <strong>{{ bucketName }}</strong></span>
      </div>
    </div>
    
    <div class="upload-wrapper fade-in" 
      @dragover.prevent="handleDragEnter"
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleFileDrop"
      :class="{ 'dragover': isDragging }"
    >
      <Card>
        <template #title>
          <div class="section-header">
            <h2>Subir Archivos</h2>
            <span class="file-types-badge">ZIP, BAM, FASTQ, VCF, CSV, TXT</span>
          </div>
        </template>
        <template #content>
          <div class="upload-instructions">
            <div class="info-message">
              <i class="pi pi-info-circle"></i>
              <div>
                <p><strong>Formatos aceptados:</strong> ZIP, BAM, BAI, FASTQ, VCF, CSV, TXT</p>
                <p><strong>Importante:</strong> El nombre del archivo debe comenzar con el ID del paciente para que se organice correctamente.</p>
                <p><strong>Patrones de nombre aceptados:</strong></p>
                <ul>
                  <li><code>99999-D_nombre.bam</code> - Formato: ID-Letra</li>
                  <li><code>99999R-TESTFILE.fastq</code> - Formato: ID+Letra-Texto</li>
                  <li><code>99999U-87628-D_datos.vcf</code> - Formato: ID+U-Número-Letra</li>
                </ul>
                <p>Ejemplos de nombres correctos: <code>60346-D_secuencia.bam</code>, <code>99998R-TEST.fastq</code></p>
              </div>
            </div>
          </div>
          
          <FileUpload
            ref="fileUpload"
            name="file"
            url="/api/v1/files/upload"
            :customUpload="true"
            @uploader="handleUpload"
            :multiple="true"
            accept=".zip,.bam,.bai,.fastq,.vcf,.csv,.txt"
            :maxFileSize="1000000000"
            chooseLabel="Seleccionar Archivos"
            uploadLabel="Subir"
            cancelLabel="Cancelar"
            :auto="false"
            class="upload-component"
            @select="onFileSelect"
          >
            <template #header="{ chooseCallback }">
              <div class="custom-upload-header" 
                @click="chooseCallback" 
                @dragover.prevent
                @drop.prevent
              >
                <i class="pi pi-cloud-upload upload-icon"></i>
                <p>Arrastra y suelta archivos aquí o haz clic para seleccionarlos</p>
              </div>
            </template>
            <template #empty>
              <!-- Reemplazado por el header personalizado -->
            </template>
          </FileUpload>
          
          <div class="text-center mt-3 mb-3">
            <Button 
              label="Subir archivos seleccionados" 
              icon="pi pi-upload" 
              v-if="selectedFilesCount > 0"
              class="p-button-primary" 
              @click="onClickUpload"
            />
          </div>

          <div v-if="success" class="success-message upload-result">
            <i class="pi pi-check-circle"></i> {{ success }}
          </div>
          <div v-if="error" class="error-message upload-result">
            <i class="pi pi-exclamation-triangle"></i> {{ error }}
          </div>
        </template>
      </Card>
    </div>
    
    <!-- Tabla de archivos subidos -->
    <Card class="recent-uploads-card fade-in">
      <template #title>
        <div class="section-header">
          <h2><i class="pi pi-list"></i> Archivos Recientes</h2>
          <Button v-if="uploadedFiles.length > 0" icon="pi pi-refresh" class="p-button-text p-button-rounded" @click="fetchMyUploadedFiles" />
        </div>
      </template>
      <template #content>
        <DataTable 
          :value="uploadedFiles" 
          :loading="loading"
          class="files-table"
          :paginator="uploadedFiles.length > 10"
          :rows="10"
          stripedRows
          responsiveLayout="scroll"
          :emptyMessage="'No se encontraron archivos'"
        >
          <Column field="original_filename" header="Nombre Original">
            <template #body="slotProps">
              <div class="file-name">
                <i :class="getFileIcon(slotProps.data.file_type)"></i>
                <span>{{ slotProps.data.original_filename }}</span>
              </div>
            </template>
          </Column>
          <Column field="patient_id" header="ID Paciente">
            <template #body="slotProps">
              <span>{{ extractPatientId(slotProps.data.s3_path) }}</span>
            </template>
          </Column>
          <Column field="s3_path" header="Ruta S3">
            <template #body="slotProps">
              <span class="text-overflow" :title="slotProps.data.s3_path">
                {{ formatS3Path(slotProps.data.s3_path) }}
              </span>
            </template>
          </Column>
          <Column field="file_type" header="Tipo"></Column>
          <Column field="size_bytes" header="Tamaño">
            <template #body="slotProps">
              <span>{{ formatFileSize(slotProps.data.size_bytes) }}</span>
            </template>
          </Column>
          <Column field="created_at" header="Fecha de Subida">
            <template #body="slotProps">
              <span>{{ formatDate(slotProps.data.created_at) }}</span>
            </template>
          </Column>
        </DataTable>
        
        <div v-if="uploadedFiles.length === 0 && !loading" class="empty-state">
          <i class="pi pi-inbox"></i>
          <p>No has subido ningún archivo todavía</p>
          <p class="empty-state-hint">Los archivos que subas aparecerán aquí.</p>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'UploadFilesView',
  data() {
    return {
      isDragging: false,
      uploading: false,
      uploadedFilesCount: 0,
      totalFiles: 0,
      uploadProgress: 0,
      showSuccessMessage: false,
      selectedFilesCount: 0
    }
  },
  computed: {
    ...mapState('files', ['uploadedFiles', 'loading', 'error', 'success', 'userCountry', 'bucketName'])
  },
  methods: {
    ...mapActions('files', ['uploadFile', 'fetchMyUploadedFiles', 'clearErrors', 'updateUserCountryAndBucket', 'fetchBucketInfo']),
    
    handleDragEnter(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },

    // Nuevo método para determinar el país según el bucket
    getCountryFromBucket(bucketName) {
      if (!bucketName) return 'Desconocido';
      
      if (bucketName.includes('bioportalarg')) return 'Argentina';
      if (bucketName.includes('bioportalmex')) return 'México';
      if (bucketName.includes('bioportalbra')) return 'Brasil';
      
      return 'Desconocido';
    },
    
    async handleUpload(event) {
      // Para cada archivo en el evento
      const validFiles = [];
      
      for (let file of event.files) {
        console.log("Intentando subir archivo:", file.name);
        
        // Verificar si el nombre del archivo tiene el formato correcto
        const regex1 = /^\d+-[A-Za-z]/;
        const regex2 = /^\d+U-\d+-[A-Za-z]/;
        const regex3 = /^\d+[A-Za-z]-/; // Nuevo patrón para archivos como 99998R-TESTTESTETS...
        
        if (!regex1.test(file.name) && !regex2.test(file.name) && !regex3.test(file.name)) {
          console.error("El nombre del archivo no tiene el formato correcto:", file.name);
          this.$store.commit('files/SET_ERROR', 
            "El nombre del archivo debe comenzar con el ID del paciente (ej: 99999-D_nombre.bam, 99999R-texto.fastq, 99999U-87628-D_nombre.vcf)"
          );
          continue;
        }
        
        validFiles.push(file);
      }
      
      // Limpiar la selección de archivos
      event.clear();
      
      // Si hay archivos válidos, iniciar la carga
      if (validFiles.length > 0) {
        await this.uploadAllFiles(validFiles);
      }
    },
    
    formatS3Path(path) {
      // Simplificar la ruta para mostrar
      return path && path.length > 40 ? path.substring(0, 40) + '...' : path
    },
    
    formatFileSize(sizeInBytes) {
      if (!sizeInBytes) return '0 B'
      
      const bytes = parseInt(sizeInBytes)
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      
      if (bytes === 0) return '0 B'
      
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
      
      return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i]
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    
    getFileIcon(fileType) {
      if (!fileType) return 'pi pi-file';
      
      if (fileType.includes('zip') || fileType.includes('archive')) return 'pi pi-file-excel';
      if (fileType.includes('fastq') || fileType.includes('bam') || fileType.includes('vcf')) return 'pi pi-file-excel';
      if (fileType.includes('csv')) return 'pi pi-file-excel';
      if (fileType.includes('txt')) return 'pi pi-file-o';
      
      return 'pi pi-file';
    },
    
    handleFileDrop(event) {
      // Prevenir el comportamiento predeterminado del navegador
      event.preventDefault();
      
      // Resetear el estado de arrastre
      this.isDragging = false;
      
      // Obtener los archivos
      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;
      
      console.log("Archivos recibidos por drag&drop:", files.length);
      
      // Validar cada archivo antes de añadirlo al FileUpload
      let validFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log("Archivo:", file.name);
        
        // Verificar si el nombre del archivo tiene el formato correcto
        const regex1 = /^\d+-[A-Za-z]/;
        const regex2 = /^\d+U-\d+-[A-Za-z]/;
        const regex3 = /^\d+[A-Za-z]-/;
        
        if (!regex1.test(file.name) && !regex2.test(file.name) && !regex3.test(file.name)) {
          console.error("El nombre del archivo no tiene el formato correcto:", file.name);
          this.$store.commit('files/SET_ERROR', 
            "El nombre del archivo debe comenzar con el ID del paciente (ej: 99999-D_nombre.bam, 99999R-texto.fastq, 99999U-87628-D_nombre.vcf)"
          );
          continue;
        }
        
        // Verificar si el tipo de archivo es válido
        const acceptedExtensions = ['.zip', '.bam', '.bai', '.fastq', '.vcf', '.csv', '.txt'];
        const fileExt = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!acceptedExtensions.includes(fileExt)) {
          console.error("Tipo de archivo no aceptado:", fileExt);
          this.$store.commit('files/SET_ERROR', 
            `El tipo de archivo ${fileExt} no está permitido. Formatos aceptados: ${acceptedExtensions.join(', ')}`
          );
          continue;
        }
        
        validFiles.push(file);
      }
      
      // Procesar archivos válidos usando el método de carga masiva
      if (validFiles.length > 0) {
        this.uploadAllFiles(validFiles);
      }
    },
    hideSuccessMessage() {
      this.showSuccessMessage = false;
      this.uploadedFilesCount = 0;
      this.totalFiles = 0;
      this.uploadProgress = 0;
    },
    
    async uploadAllFiles(files) {
      if (!files || files.length === 0) return;
      
      // Iniciar proceso de carga
      this.uploading = true;
      this.uploadedFilesCount = 0;
      this.totalFiles = files.length;
      this.uploadProgress = 0;
      
      let successCount = 0;
      let errorCount = 0;
      
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          console.log(`Subiendo archivo ${i+1}/${files.length}: ${file.name}`);
          
          try {
            const result = await this.uploadFile(file);
            if (result.success) {
              successCount++;
            } else {
              errorCount++;
              console.error(`Error al subir ${file.name}:`, result.error);
            }
          } catch (error) {
            errorCount++;
            console.error(`Excepción al subir ${file.name}:`, error);
          }
          
          // Actualizar progreso
          this.uploadedFilesCount++;
          this.uploadProgress = Math.round((this.uploadedFilesCount / this.totalFiles) * 100);
        }
        
        // Mostrar mensaje de éxito/resumen
        if (successCount > 0) {
          if (errorCount > 0) {
            this.$store.commit('files/SET_SUCCESS', 
              `Se subieron ${successCount} archivos correctamente. ${errorCount} archivos fallaron.`
            );
          } else {
            this.$store.commit('files/SET_SUCCESS', 
              `Se subieron ${successCount} archivos correctamente.`
            );
          }
          
          // Mostrar overlay de éxito
          this.showSuccessMessage = true;
        } else if (errorCount > 0) {
          this.$store.commit('files/SET_ERROR', 
            `No se pudo subir ningún archivo. ${errorCount} archivos fallaron.`
          );
        }
        
      } catch (error) {
        console.error("Error general en la carga:", error);
        this.$store.commit('files/SET_ERROR', "Error al subir los archivos");
      } finally {
        // Finalizar el proceso de carga
        this.uploading = false;
      }
    },
    onFileSelect(event) {
      console.log("Archivos seleccionados:", event.files);
      
      // Validar y procesar los archivos seleccionados
      const validFiles = [];
      
      for (let file of event.files) {
        console.log("Verificando archivo:", file.name);
        
        // Verificar si el nombre del archivo tiene el formato correcto
        const regex1 = /^\d+-[A-Za-z]/;
        const regex2 = /^\d+U-\d+-[A-Za-z]/;
        const regex3 = /^\d+[A-Za-z]-/;
        
        if (!regex1.test(file.name) && !regex2.test(file.name) && !regex3.test(file.name)) {
          console.error("El nombre del archivo no tiene el formato correcto:", file.name);
          this.$store.commit('files/SET_ERROR', 
            "El nombre del archivo debe comenzar con el ID del paciente (ej: 99999-D_nombre.bam, 99999R-texto.fastq, 99999U-87628-D_nombre.vcf)"
          );
          continue;
        }
        
        // Verificar si el tipo de archivo es válido
        const acceptedExtensions = ['.zip', '.bam', '.bai', '.fastq', '.vcf', '.csv', '.txt'];
        const fileExt = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!acceptedExtensions.includes(fileExt)) {
          console.error("Tipo de archivo no aceptado:", fileExt);
          this.$store.commit('files/SET_ERROR', 
            `El tipo de archivo ${fileExt} no está permitido. Formatos aceptados: ${acceptedExtensions.join(', ')}`
          );
          continue;
        }
        
        validFiles.push(file);
      }
      
      // Actualizar contador de archivos seleccionados
      this.selectedFilesCount = validFiles.length;
      
      // Opcionalmente, podemos iniciar la carga automáticamente
      if (validFiles.length > 0) {
        this.uploadAllFiles(validFiles);
      }
      
      // Limpiar la selección después de procesar
      this.$refs.fileUpload.clear();
    },
    onClickUpload() {
      // Ya que usamos customUpload, podemos obtener los archivos directamente del componente
      const files = this.$refs.fileUpload.files;
      
      if (files && files.length > 0) {
        this.uploadAllFiles(files);
        this.selectedFilesCount = 0;
        this.$refs.fileUpload.clear();
      } else {
        console.warn("No hay archivos seleccionados para subir");
        this.$store.commit('files/SET_ERROR', "No hay archivos seleccionados para subir");
      }
    },
    extractPatientId(s3Path) {
      if (!s3Path) return 'N/A';
      
      // Intenta extraer el ID del formato paciente-XXXXX/
      const patientMatch = s3Path.match(/paciente-(\d+)\//);
      if (patientMatch && patientMatch[1]) {
        return patientMatch[1];
      }
      
      // Si no encuentra el formato anterior, intenta extraer de la primera parte del nombre del archivo
      const parts = s3Path.split('/');
      const fileName = parts[parts.length - 1];
      
      // Busca patrones como 60346-D, 99998R o 60346U-87628-R
      const idMatch = fileName.match(/^(\d+)(?:[A-Za-z]-|[A-Za-z])/);
      if (idMatch && idMatch[1]) {
        return idMatch[1];
      }
      
      return 'N/A';
    }
  },
  created() {
    // Cargar los archivos subidos al iniciar
    this.fetchMyUploadedFiles()
    
    // Actualizar la información del país y bucket
    this.updateUserCountryAndBucket()
    this.fetchBucketInfo()
    
    // Limpiar errores
    this.clearErrors()
  }
}
</script>

<style scoped>
.upload-container {
  max-width: 1200px;
  padding-bottom: 40px;
}

.upload-wrapper {
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.3rem;
  color: var(--primary-dark);
}

.file-types-badge {
  background-color: var(--primary-light);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.upload-instructions {
  margin-bottom: 25px;
}

.info-message {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background-color: var(--surface-hover);
  border-radius: 8px;
  margin-bottom: 25px;
}

.info-message i {
  font-size: 1.5rem;
  color: var(--info-color);
  margin-top: 3px;
}

.info-message p {
  margin: 0 0 8px 0;
  color: var(--text-color);
}

.info-message ul {
  margin: 5px 0 10px 20px;
  padding: 0;
}

.info-message li {
  margin-bottom: 5px;
}

.info-message code {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 2px 5px;
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--primary-dark);
}

.upload-component {
  width: 100%;
}

.custom-upload-header {
  border: 3px dashed var(--primary-light);
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: rgba(25, 118, 210, 0.05);
  margin-bottom: 20px;
  position: relative;
}

.custom-upload-header:hover,
.upload-wrapper.dragover .custom-upload-header {
  border-color: var(--primary-color);
  background-color: rgba(25, 118, 210, 0.08);
  transform: scale(1.01);
}

.upload-icon {
  font-size: 3.5rem;
  color: var(--primary-color);
  margin-bottom: 20px;
  display: block;
}

.custom-upload-header p {
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 0;
}

.upload-result {
  margin: 20px 0 10px;
  padding: 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: fadeIn 0.3s ease;
}

.success-message {
  background-color: rgba(46, 125, 50, 0.1);
  color: #2e7d32;
}

.error-message {
  background-color: rgba(211, 47, 47, 0.1);
  color: #d32f2f;
}

.recent-uploads-card {
  margin-top: 30px;
}

.files-table {
  width: 100%;
}

.file-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-name i {
  color: var(--primary-color);
}

.text-overflow {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 15px;
  color: #d0d0d0;
}

.empty-state p {
  margin: 5px 0;
  font-size: 1.1rem;
}

.empty-state-hint {
  font-size: 0.9rem !important;
  opacity: 0.7;
}

@media (max-width: 768px) {
  .upload-container {
    padding: 0 10px 40px;
  }
  
  .custom-upload-header {
    padding: 20px;
  }
  
  .upload-icon {
    font-size: 2.5rem;
  }
}

.bucket-info {
  display: flex;
  gap: 15px;
  margin-top: 10px;
}

.country-badge,
.bucket-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.country-badge {
  background-color: rgba(30, 58, 138, 0.1);
  color: var(--primary-dark);
}

.bucket-badge {
  background-color: rgba(211, 47, 47, 0.1);
  color: #d32f2f;
}

.bucket-info i {
  font-size: 1rem;
}

.upload-overlay,
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.upload-overlay-content,
.success-overlay-content {
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.upload-spinner {
  font-size: 3rem;
  color: var(--primary-color);
  margin-bottom: 20px;
  display: block;
}

.success-icon {
  font-size: 3.5rem;
  color: #2e7d32;
  margin-bottom: 20px;
  display: block;
}

.upload-overlay-content h3,
.success-overlay-content h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.upload-overlay-content p,
.success-overlay-content p {
  margin-bottom: 20px;
  color: var(--text-color-secondary);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.text-center {
  text-align: center;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.logo-header {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.header-logo {
  height: 50px;
  width: auto;
  background-color: #0d0264;
  padding: 8px;
  border-radius: 8px;
}
</style> 